import type { FC } from "react";
import { useCallback, useEffect, useRef } from "react";
import { BookOpen01, ChevronSelectorVertical, LogOut01, Settings01, User01 } from "@untitledui/icons";
import { useFocusManager } from "react-aria";
import type { DialogProps as AriaDialogProps, Placement as AriaPlacement } from "react-aria-components";
import { Button as AriaButton, Dialog as AriaDialog, DialogTrigger as AriaDialogTrigger, Link as AriaLink, Popover as AriaPopover } from "react-aria-components";
import { AvatarLabelGroup } from "../../../base/avatar/avatar-label-group";
import { useBreakpoint } from "../../../../hooks/use-breakpoint";
import { cx } from "../../../../utils/cx";
import { ThemeSegmented } from "../../../../theme/theme";

export type NavAccountType = {
    /** Unique identifier. */
    id: string;
    /** Display name of the account holder. */
    name: string;
    /** Secondary line (email, mobile, or role). */
    email: string;
    /** Avatar image URL. Leave empty to show initials (no external images at runtime). */
    avatar?: string;
    /** Online status indicator. */
    status?: "online" | "offline";
};

export type NavAccountMenuItem = {
    label: string;
    icon?: FC<{ className?: string }>;
    href?: string;
    onAction?: () => void;
};

const defaultMenuItems: NavAccountMenuItem[] = [
    { label: "پروفایل و حساب", icon: User01, href: "/account" },
    { label: "تنظیمات اعلان", icon: Settings01, href: "/account" },
    { label: "راهنما", icon: BookOpen01, href: "/how-it-works" },
];

const initialsOf = (name: string) =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("\u200c");

export const NavAccountMenu = ({
    className,
    items = defaultMenuItems,
    onSignOut,
    ...dialogProps
}: AriaDialogProps & { className?: string; items?: NavAccountMenuItem[]; onSignOut?: () => void }) => {
    const focusManager = useFocusManager();
    const dialogRef = useRef<HTMLDivElement>(null);

    const onKeyDown = useCallback(
        (e: KeyboardEvent) => {
            switch (e.key) {
                case "ArrowDown":
                    focusManager?.focusNext({ tabbable: true, wrap: true });
                    break;
                case "ArrowUp":
                    focusManager?.focusPrevious({ tabbable: true, wrap: true });
                    break;
            }
        },
        [focusManager],
    );

    useEffect(() => {
        const element = dialogRef.current;
        if (element) {
            element.addEventListener("keydown", onKeyDown);
        }

        return () => {
            if (element) {
                element.removeEventListener("keydown", onKeyDown);
            }
        };
    }, [onKeyDown]);

    return (
        <AriaDialog
            {...dialogProps}
            ref={dialogRef}
            aria-label="منوی حساب"
            className={cx("w-66 rounded-xl bg-secondary_alt shadow-lg ring ring-secondary_alt outline-hidden", className)}
        >
            <div className="rounded-xl bg-primary ring-1 ring-secondary">
                <div className="flex flex-col gap-0.5 py-1.5">
                    {items.map((item) => (
                        <NavAccountCardMenuItem key={item.label} label={item.label} icon={item.icon} href={item.href} onClick={item.onAction} />
                    ))}
                </div>
            </div>

            <div className="flex flex-col gap-1.5 px-2.5 pt-2.5 pb-1">
                <span className="text-xs font-medium text-tertiary">تم نمایش</span>
                <ThemeSegmented />
            </div>

            <div className="pt-1 pb-1.5">
                <NavAccountCardMenuItem label="خروج" icon={LogOut01} onClick={onSignOut} />
            </div>
        </AriaDialog>
    );
};

const NavAccountCardMenuItem = ({
    icon: Icon,
    label,
    href,
    onClick,
}: {
    icon?: FC<{ className?: string }>;
    label: string;
    href?: string;
    onClick?: () => void;
}) => {
    const inner = (
        <div
            className={cx(
                "flex w-full items-center justify-between gap-3 rounded-md p-2 group-hover/item:bg-primary_hover",
                "outline-focus-ring group-focus-visible/item:outline-2 group-focus-visible/item:outline-offset-2",
            )}
        >
            <div className="flex gap-2 text-sm font-semibold text-secondary group-hover/item:text-secondary_hover">
                {Icon && <Icon className="size-5 text-fg-quaternary group-hover/item:text-fg-quaternary_hover" />} {label}
            </div>
        </div>
    );

    if (href) {
        return (
            <AriaLink href={href} className="group/item block w-full cursor-pointer px-1.5 focus:outline-hidden">
                {inner}
            </AriaLink>
        );
    }

    return (
        <button type="button" onClick={onClick} className="group/item w-full cursor-pointer px-1.5 text-start focus:outline-hidden">
            {inner}
        </button>
    );
};

export const NavAccountCard = ({
    popoverPlacement,
    account,
    menuItems,
    onSignOut,
    avatarRounded,
}: {
    popoverPlacement?: AriaPlacement;
    account: NavAccountType;
    menuItems?: NavAccountMenuItem[];
    onSignOut?: () => void;
    avatarRounded?: boolean;
}) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const isDesktop = useBreakpoint("lg");

    return (
        <div ref={triggerRef} className="relative flex items-center gap-3 rounded-xl p-3 ring-1 ring-secondary ring-inset">
            <AvatarLabelGroup
                size="md"
                src={account.avatar || null}
                initials={initialsOf(account.name)}
                title={account.name}
                subtitle={account.email}
                status={account.status}
                rounded={avatarRounded}
            />

            <AriaDialogTrigger>
                <AriaButton
                    aria-label="منوی حساب"
                    className="absolute top-2 end-2 flex cursor-pointer items-center justify-center rounded-md p-1.5 text-fg-quaternary outline-focus-ring transition duration-100 ease-linear hover:bg-primary_hover hover:text-fg-quaternary_hover focus-visible:outline-2 focus-visible:outline-offset-2 pressed:bg-primary_hover pressed:text-fg-quaternary_hover"
                >
                    <ChevronSelectorVertical className="size-4 shrink-0 stroke-[2.25px]" />
                </AriaButton>
                <AriaPopover
                    // RTL: logical "end" so the menu opens toward the content in both directions.
                    placement={popoverPlacement ?? (isDesktop ? "end bottom" : "top end")}
                    triggerRef={triggerRef}
                    offset={8}
                    className={({ isEntering, isExiting }) =>
                        cx(
                            "origin-(--trigger-anchor-point) will-change-transform",
                            isEntering &&
                                "duration-150 ease-out animate-in fade-in placement-left:slide-in-from-right-0.5 placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5",
                            isExiting &&
                                "duration-100 ease-in animate-out fade-out placement-left:slide-out-to-right-0.5 placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5",
                        )
                    }
                >
                    <NavAccountMenu items={menuItems} onSignOut={onSignOut} />
                </AriaPopover>
            </AriaDialogTrigger>
        </div>
    );
};
